import { useEffect, useRef, useState } from "react";
import type { Result, FieldSave, FieldAria } from "@/components/ui/box.schema";
import { Input } from "@/components/ui/input";
import { currentOnConflict } from "@/lib/api/conflict";
import { ConflictNotice } from "@/components/ui/conflict-notice";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import type { GradeItemType } from "@/lib/api/gradebook";
import { registerCommit, recorded, registerCopy, registerText } from "@/lib/register-copy";
import { Draft, validWeight } from "./assessment-line-draft";

export function LineFields({
  id,
  draft,
  set,
}: {
  id: string;
  draft: Draft;
  set: (value: Draft) => void;
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-3">
      <div>
        <Label htmlFor={`${id}-name`}>{registerText.name}</Label>
        <Input
          id={`${id}-name`}
          value={draft.label}
          aria-invalid={!draft.label.trim() || undefined}
          onChange={(e) => set({ ...draft, label: e.target.value })}
        />
      </div>
      <div>
        <Label htmlFor={`${id}-type`}>{registerText.type}</Label>
        <NativeSelect
          id={`${id}-type`}
          value={draft.type}
          onChange={(e) =>
            set({ ...draft, type: e.target.value as GradeItemType })
          }
        >
          {Object.entries(registerCopy.lineTypes).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </NativeSelect>
      </div>
      <div>
        <Label htmlFor={`${id}-weight`}>{registerText.weight}</Label>
        <Input
          id={`${id}-weight`}
          type="number"
          min={0}
          max={100}
          value={draft.weight}
          aria-invalid={!validWeight(draft.weight) || undefined}
          onChange={(e) => set({ ...draft, weight: e.target.value })}
        />
      </div>
    </div>
  );
}

type SavedLineValue = {
  value: string;
  version?: string;
  write: (value: string, key: string, version: string) => Promise<unknown>;
};

export function AssessmentCell({
  label,
  aria,
  value,
  version,
  write,
  readNewer,
  refresh,
  onSave,
  valid,
  lastRow,
  onDirty,
}: SavedLineValue & {
  label: string;
  aria: FieldAria;
  readNewer: () => Promise<SavedLineValue>;
  refresh: () => Promise<void>;
  onSave: (value: FieldSave) => void;
  valid: (value: string) => boolean;
  lastRow: boolean;
  onDirty: (value: boolean) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [conflict, setConflict] = useState<SavedLineValue | null>(null);
  const [unknown, setUnknown] = useState<Result | null>(null);
  const base = useRef<SavedLineValue>({ value, version, write });
  const flight = useRef(false);
  const dirty = useRef(false);
  useEffect(() => {
    if (!dirty.current && !flight.current &&
      (!base.current.version || version === base.current.version || Date.parse(version ?? "") > Date.parse(base.current.version))) {
      base.current = { value, version, write };
      setDraft(value);
    }
  }, [value, version, write, draft]);
  useEffect(() => { onDirty(draft !== base.current.value || Boolean(unknown)); }, [draft, value, unknown, onDirty]);
  const send = async () => {
    if (flight.current || conflict || unknown || draft === base.current.value)
      return;
    if (!valid(draft)) {
      onSave({
        status: "refused",
        sentence: registerText.enterAWholeNumberInThisFieldSRange,
      });
      return;
    }
    if (!base.current.version) {
      onSave({
        status: "refused",
        sentence: registerText.readTheLatestAssessmentLinesBeforeSaving,
      });
      return;
    }
    flight.current = true;
    onSave({ status: "saving" });
    const held = draft;
    const key = crypto.randomUUID();
    const next = await registerCommit(
      key,
      async () => {
        try {
          return await base.current.write(held, key, base.current.version!);
        } catch (error) {
          if (currentOnConflict(error)) setConflict(await readNewer());
          throw error;
        }
      },
      async () => {
        base.current = { ...base.current, value: held };
        dirty.current = false;
        await refresh();
        return recorded([registerText.assessmentFieldSaved]);
      },
    );
    flight.current = false;
    if (next.state === "confirmed") onSave({ status: "saved" });
    else if (next.state === "refused")
      onSave({ status: "refused", sentence: next.sentence });
    else if (next.state === "unknown") {
      setUnknown(next);
      onSave({ status: "unknown", check: async () => {
        const checked = await next.check.run();
        if (checked.state === "confirmed") { setUnknown(null); onSave({ status: "saved" }); }
        else if (checked.state === "refused") { setUnknown(null); onSave({ status: "refused", sentence: checked.sentence }); }
      } });
    }
  };
  return (
    <div className="grid min-w-20 gap-2">
      <Input
        aria-labelledby={label}
        {...aria}
        inputMode="numeric"
        value={draft}
        disabled={unknown?.state === "unknown"}
        onChange={(event) => {
          dirty.current = true;
          setDraft(event.target.value);
          onSave({ status: "idle" });
        }}
        onBlur={() => void send()}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            dirty.current = false;
            setDraft(base.current.value);
            onSave({ status: "idle" });
          } else if (event.key === "Enter" && lastRow && !event.shiftKey) {
            event.preventDefault();
            event.stopPropagation();
            void send();
          }
        }}
      />
      {conflict && (
        <ConflictNotice
          object={registerText.assessmentField}
          message={registerText.thisAssessmentFieldChangedYourDraftIsKept}
          facts={[
            { label: registerText.savedValue, theirs: conflict.value },
            { label: registerText.yourDraft, theirs: draft },
          ]}
          keepMineLabel={registerText.keepMyValueOnTheNewerLine}
          loadTheirsLabel={registerText.loadTheNewerValue}
          onKeepMine={() => {
            base.current = conflict;
            setConflict(null);
          }}
          onLoadTheirs={() => {
            base.current = conflict;
            dirty.current = false;
            setDraft(conflict.value);
            setConflict(null);
            onSave({ status: "idle" });
          }}
        />
      )}

    </div>
  );
}
